import intl from "react-intl-universal";
import { Link } from "react-router-dom";
import { TbChevronRight } from "react-icons/tb";
import { CrossborderCopyButton } from "../../../Crossborder/components/CrossborderCopyButton";

const BORDER_CARD =
  "rounded-xl border border-gray-200 bg-white dark:border-brand-dark-border dark:bg-brand-dark-surface";

const formatDateTime = (value) => {
  if (!value) return null;
  return new Intl.DateTimeFormat(undefined, { dateStyle: "short", timeStyle: "short" }).format(
    new Date(value),
  );
};

const formatAccountType = (type) => {
  if (!type) return null;
  const normalized = String(type).trim().toLowerCase();
  // OTC is internal — user-facing type is WALLET.
  if (normalized === "otc" || normalized === "otc_virtual") {
    return intl.get("baas.accounts.type.wallet") || "WALLET";
  }
  const key = `baas.accounts.type.${type}`;
  const label = intl.get(key);
  return label && label !== key ? label : type;
};

/** ISO 3166-1 alpha-2 → localized region name, e.g. "Estados Unidos (US)". */
const formatCountryLabel = (countryCode) => {
  const code = String(countryCode || "")
    .trim()
    .toUpperCase();
  if (!code) return null;
  if (code.length !== 2) return code;
  try {
    const locale = intl.getInitOptions()?.currentLocale || "pt-BR";
    const name = new Intl.DisplayNames([locale], { type: "region" }).of(code);
    return name ? `${name} (${code})` : code;
  } catch {
    return code;
  }
};

const METADATA_FIELDS = [
  { key: "externalAccountId", labelKey: "baas.accounts.metadata.fields.externalAccountId" },
  { key: "ledgerAccountId", labelKey: "baas.accounts.metadata.fields.ledgerAccountId" },
  { key: "providerRef", labelKey: "baas.accounts.metadata.fields.providerRef" },
];

const DetailRow = ({ label, value, mono = false, copyValue, children }) => {
  if (!children && (value == null || value === "")) return null;

  return (
    <div className="border-b border-gray-100 py-4 last:border-b-0 dark:border-brand-dark-border sm:py-5">
      <span className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
        {label}
      </span>
      {children ? (
        children
      ) : (
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={`min-w-0 text-sm font-medium text-brand-dark dark:text-gray-100 ${mono ? "break-all font-mono text-xs" : ""}`}
          >
            {value}
          </span>
          {copyValue ? (
            <CrossborderCopyButton
              value={copyValue}
              ariaLabel={intl.get("baas.accounts.metadata.copyValue", { field: label })}
            />
          ) : null}
        </div>
      )}
    </div>
  );
};

/**
 * Shared account metadata + linked entity (single card).
 * Per-currency balances + banking details live in {@link BaasAccountCurrencyBalanceCards}.
 */
export const BaasAccountInfoPanel = ({ account }) => {
  const entityLabel = account.entityName || account.entityId || null;
  const entityTo = account.entityId
    ? `/baas/entidades?entityId=${encodeURIComponent(account.entityId)}`
    : "/baas/entidades";

  return (
    <section>
      <p className="dashboard-section-label mb-3">
        {intl.get("baas.accounts.detail.accountInfo")}
      </p>
      <div className={`${BORDER_CARD} px-5 sm:px-6`}>
        <div className="grid gap-x-10 gap-y-0 sm:grid-cols-2 lg:gap-x-14">
          <DetailRow label={intl.get("baas.accounts.columns.alias")} value={account.alias} />
          <DetailRow
            label={intl.get("baas.accounts.columns.type")}
            value={formatAccountType(account.type)}
          />
          <DetailRow
            label={intl.get("baas.accounts.detail.country")}
            value={formatCountryLabel(account.countryCode)}
          />
          <DetailRow label={intl.get("baas.accounts.columns.entity")}>
            <div className="space-y-2">
              {entityLabel ? (
                <Link
                  to={entityTo}
                  className="inline-flex max-w-full items-center gap-1 text-sm font-semibold text-[#e7a73f] transition hover:underline"
                >
                  <span className="min-w-0 truncate">{entityLabel}</span>
                  <TbChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
                </Link>
              ) : (
                <span className="text-sm font-medium text-brand-dark dark:text-gray-100">—</span>
              )}
              {account.entityId ? (
                <div className="flex min-w-0 items-center gap-2">
                  <span className="break-all font-mono text-xs text-gray-500 dark:text-gray-400">
                    {account.entityId}
                  </span>
                  <CrossborderCopyButton
                    value={account.entityId}
                    ariaLabel={intl.get("baas.accounts.metadata.copyValue", {
                      field: intl.get("baas.accounts.detail.entityId"),
                    })}
                  />
                </div>
              ) : null}
            </div>
          </DetailRow>
          <DetailRow
            label={intl.get("baas.accounts.detail.accountId")}
            value={account.id}
            mono
            copyValue={account.id}
          />
          <DetailRow
            label={intl.get("baas.accounts.columns.createdAt")}
            value={account.createdAt ? formatDateTime(account.createdAt) : null}
          />
          {METADATA_FIELDS.map((field) => (
            <DetailRow
              key={field.key}
              label={intl.get(field.labelKey)}
              value={account.metadata?.[field.key]}
              mono
              copyValue={account.metadata?.[field.key] || undefined}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default BaasAccountInfoPanel;
